// Deps
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import classnames from 'classnames';

// UI Components
import { noop } from 'ui-components/default-props/noop';

// Default PropTypes
import { emptyArray } from 'ui-components/default-props/empty';

import { FulfillmentPlanPropType } from '../../prop-types/fulfillment-plan';
import { MessageDescriptorPropType } from '../../prop-types/intl';

import { ShipmentStatusService } from '../../services/shipment-status-service';

// Enums
import { FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';

// Components
import MultiPackageList from '../../components/multi-package-list';
import MultiPackageBadge from '../../components/multi-package-badge';
import DrawerShipmentConfiguration from '../../components/orders-drawer/sections/single-order-csw/fulfillment-configuration/shipment';

import ContextMenu from './shipment-object-header/context-menu';
import style from './shipment-object.module.less';
import ShipmentStatus from '../shipment-status/index';

const defaultShipment = {
  shipDateTime: '',
};

const ShipmentObject = ({
  shipment = defaultShipment,
  isExpandedByDefault = false,
  fulfillments = emptyArray,
  actions = emptyArray,
  markLabelPrinted = noop,
  markPackingSlipPrinted = noop,
  className = '',
}) => {
  const expanded = isExpandedByDefault;

  const packages = shipment?.packages ?? emptyArray;
  const packageCount = packages.length;
  const status = ShipmentStatusService.buildStatusFromFulfillments(shipment, fulfillments);
  const fulfillmentPlanId = shipment?.fulfillmentPlanId;
  const fulfillmentIds = fulfillments.map(({ fulfillmentId }) => fulfillmentId);
  const {
    labelPrintedStatus,
    labelCreatedDateTime,
    packingSlipPrinted,
    packingSlipPrintedStatus,
    marketplaceNotification,
    shipmentEmailNotification,
    deliveryEmailNotification,
  } = status;

  const orderGridStatus = FulfillmentPlanOrderGridStatus[shipment?.orderGridStatus ?? ''];
  const isShipmentShipped = orderGridStatus?.name === 'Shipped';

  const markLabelPrintedCallback = useCallback(() => {
    markLabelPrinted([fulfillmentPlanId], fulfillmentIds, !labelPrintedStatus);
  }, [markLabelPrinted, fulfillmentPlanId, fulfillmentIds, labelPrintedStatus]);
  const markPackingSlipPrintedCallback = useCallback(() => {
    markPackingSlipPrinted(fulfillmentPlanId, !packingSlipPrinted);
  }, [markPackingSlipPrinted, fulfillmentPlanId, packingSlipPrinted]);

  return (
    <div className={classnames(style['shipment-object-container'], className)}>
      <div className={style['shipment-object']}>
        {isShipmentShipped && (
          <React.Fragment>
            <div className={style['status-and-context']}>
              <ShipmentStatus
                deliveryEmailNotification={deliveryEmailNotification}
                labelCreatedDateTime={labelCreatedDateTime}
                labelPrintedStatus={labelPrintedStatus}
                marketplaceNotification={marketplaceNotification}
                markLabelPrinted={markLabelPrintedCallback}
                markPackingSlipPrinted={markPackingSlipPrintedCallback}
                packingSlipPrintedStatus={packingSlipPrintedStatus}
                shipmentEmailNotification={shipmentEmailNotification}
              />
              <ContextMenu actions={actions} fulfillments={fulfillments} shipment={shipment} />
              {packageCount > 1 && <MultiPackageBadge isExpanded={expanded} packageCount={packageCount} />}
            </div>
          </React.Fragment>
        )}
        {!isShipmentShipped && <DrawerShipmentConfiguration fulfillmentPlanId={shipment.fulfillmentPlanId} />}
      </div>
      {packageCount > 1 && expanded && <MultiPackageList packages={packages} />}
    </div>
  );
};

ShipmentObject.propTypes = {
  shipment: FulfillmentPlanPropType,
  isExpandedByDefault: PropTypes.bool,
  fulfillments: PropTypes.array,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      translation: MessageDescriptorPropType,
      handler: PropTypes.func,
      disabled: PropTypes.bool,
    }),
  ),
  markLabelPrinted: PropTypes.func,
  markPackingSlipPrinted: PropTypes.func,
  className: PropTypes.string,
};

export default ShipmentObject;
